/**
 * 356 (research R12): one formatter for settings values and labels, used by the card diffs, the
 * "Last changed" line and the History tab. History stores raw values (sen, basis points, minutes,
 * enum strings, booleans); this turns them into what staff read. Mirrors the backend's
 * `platform-config/settingsCatalogue.ts`.
 */
export type SettingKey =
  | 'platform_fee'
  | 'sst'
  | 'payment_channels'
  | 'operating_hours'
  | 'extension_policy'
  | 'inspection_photos'
  | 'booking_buffer'
  | 'unpaid_bookings'
  | 'auto_workflow'
  | 'terms_of_service'
  | 'privacy_policy'
  | 'home_popup'
  | 'receipt_details'
  | 'booking_emails'
  | 'penalty_payment'
  | 'mileage_packages';

export type SettingsGroup =
  | 'money'
  | 'booking_rules'
  | 'automation'
  | 'legal'
  | 'app'
  | 'notifications';

export const SETTING_LABELS: Record<SettingKey, string> = {
  platform_fee: 'Platform fee',
  sst: 'SST',
  payment_channels: 'Payment channels',
  operating_hours: 'Operating hours',
  extension_policy: 'Rental extensions',
  inspection_photos: 'Customer inspection photos',
  booking_buffer: 'Gap between rentals',
  unpaid_bookings: 'Unpaid bookings',
  auto_workflow: 'Automatic booking workflow',
  terms_of_service: 'Terms of service',
  privacy_policy: 'Privacy policy',
  home_popup: 'Home pop-up',
  receipt_details: 'Receipt details',
  booking_emails: 'Booking emails',
  penalty_payment: 'Penalties',
  mileage_packages: 'Mileage packages',
};

export const SETTING_GROUP: Record<SettingKey, SettingsGroup> = {
  platform_fee: 'money',
  sst: 'money',
  payment_channels: 'money',
  operating_hours: 'booking_rules',
  extension_policy: 'booking_rules',
  inspection_photos: 'booking_rules',
  booking_buffer: 'booking_rules',
  unpaid_bookings: 'automation',
  auto_workflow: 'automation',
  terms_of_service: 'legal',
  privacy_policy: 'legal',
  home_popup: 'app',
  receipt_details: 'app',
  booking_emails: 'notifications',
  penalty_payment: 'app',
  mileage_packages: 'money',
};

export const GROUP_LABELS: Record<SettingsGroup, string> = {
  money: 'Money',
  booking_rules: 'Booking rules',
  automation: 'Automation',
  legal: 'Legal',
  app: 'App',
  notifications: 'Notifications',
};

/**
 * 459: Bayarcash channel names. A small static lookup, duplicated from the backend's
 * `payments/channel-names.ts` deliberately, since `admin/` and `backend/` share no code.
 */
export const CHANNEL_NAMES: Record<string, string> = {
  '1': 'FPX Online Banking',
  '2': 'Manual Transfer',
  '3': 'Direct Debit',
  '4': 'FPX Line of Credit',
  '5': 'DuitNow Online Banking / Wallets',
  '6': 'DuitNow QR',
  '8': 'Boost PayFlex',
  '9': 'QRIS Online Banking',
  '10': 'QRIS e-Wallet',
  '11': 'NETS (Singapore)',
};

export function channelName(id: string): string {
  return CHANNEL_NAMES[id] ?? `Payment channel ${id}`;
}

export const MODE_LABELS: Record<string, string> = { off: 'Off', count: 'Count only', on: 'On' };

/** 395 (#1336): the home pop-up's choices, as the card names them. */
export const FREQUENCY_LABELS: Record<string, string> = {
  every_visit: 'Every visit',
  daily: 'Once a day',
  per_change: 'Once per change',
};

export const PRECEDENCE_LABELS: Record<string, string> = {
  car: 'Car hours first',
  location: 'Location hours first',
  intersection: 'Intersection (both must overlap)',
};

type Kind =
  | 'money'
  | 'bps'
  | 'minutes'
  | 'maxMinutes'
  | 'mode'
  | 'onOff'
  | 'yesNo'
  | 'precedence'
  | 'version'
  | 'text'
  // 393: a picture's id, said as words (an id means nothing to staff).
  | 'picture'
  // 395: the home pop-up's How often.
  | 'frequency'
  | 'km';

interface FieldSpec {
  label: string;
  kind: Kind;
}

const FIELDS: Record<string, FieldSpec> = {
  'platform_fee:enabled': { label: 'Platform fee', kind: 'onOff' },
  'platform_fee:amount_minor': { label: 'Amount', kind: 'money' },
  'platform_fee:taxable': { label: 'Taxable', kind: 'yesNo' },
  'platform_fee:refundable': { label: 'Refundable', kind: 'yesNo' },
  'sst:enabled': { label: 'SST', kind: 'onOff' },
  'sst:rate_bps': { label: 'Rate', kind: 'bps' },
  'operating_hours:value': { label: 'Precedence', kind: 'precedence' },
  'extension_policy:min_notice_minutes': { label: 'Minimum notice', kind: 'minutes' },
  'extension_policy:max_total_extension_minutes': {
    label: 'Maximum total extension',
    kind: 'maxMinutes',
  },
  'inspection_photos:value': { label: 'Customer inspection photos', kind: 'onOff' },
  'inspection_photos:pickup_before_minutes': {
    label: 'Pickup photos open before',
    kind: 'minutes',
  },
  'inspection_photos:pickup_after_minutes': { label: 'Pickup photos close after', kind: 'minutes' },
  'inspection_photos:return_before_minutes': {
    label: 'Return photos open before',
    kind: 'minutes',
  },
  'inspection_photos:return_after_minutes': { label: 'Return photos close after', kind: 'minutes' },
  'booking_buffer:value': { label: 'Gap between rentals', kind: 'minutes' },
  'unpaid_bookings:hold_minutes': { label: 'Payment hold', kind: 'minutes' },
  'unpaid_bookings:unpaid_cancel_mode': { label: 'Auto-cancel', kind: 'mode' },
  'unpaid_bookings:unpaid_cancel_minutes': { label: 'Cancel after', kind: 'minutes' },
  'auto_workflow:mode': { label: 'Mode', kind: 'mode' },
  'terms_of_service:version': { label: 'Version', kind: 'version' },
  'privacy_policy:version': { label: 'Version', kind: 'version' },
  // 389: the posters are recorded as one readable line each side (backend research R3).
  'home_popup:enabled': { label: 'Home pop-up', kind: 'onOff' },
  'home_popup:posters': { label: 'Posters', kind: 'text' },
  // 395 (#1336)
  'home_popup:frequency': { label: 'How often', kind: 'frequency' },
  // 390: the receipt's issuer.
  'receipt_details:company_name': { label: 'Company name', kind: 'text' },
  'receipt_details:registration_no': { label: 'Registration no.', kind: 'text' },
  'receipt_details:address': { label: 'Address', kind: 'text' },
  'receipt_details:sst_no': { label: 'SST no.', kind: 'text' },
  // 392: each booking event's email switch.
  'booking_emails:confirmed': { label: 'Booking confirmed: email', kind: 'onOff' },
  'booking_emails:cancelled': { label: 'Booking cancelled: email', kind: 'onOff' },
  'booking_emails:awaiting_payment': { label: 'Created by staff: email', kind: 'onOff' },
  'booking_emails:pickup_reminder': { label: 'Pickup reminder: email', kind: 'onOff' },
  'booking_emails:pickup_soon': { label: 'Pickup soon: email', kind: 'onOff' },
  'booking_emails:return_reminder': { label: 'Return reminder: email', kind: 'onOff' },
  'booking_emails:return_soon': { label: 'Return soon: email', kind: 'onOff' },
  'booking_emails:status_update': { label: 'Status update: email', kind: 'onOff' },
  'booking_emails:changed': { label: 'Changed by staff: email', kind: 'onOff' },
  // 393 (#1313)
  'booking_emails:penalty_recorded': { label: 'Penalty recorded: email', kind: 'onOff' },
  // 407 (#1370)
  'booking_emails:return_overdue': { label: 'Return time passed: email', kind: 'onOff' },
  'penalty_payment:qr_picture_id': { label: 'QR picture', kind: 'picture' },
  // 409 (#1471)
  'penalty_payment:commission_rate_bps': { label: 'CARii commission', kind: 'bps' },
  // 417 (#1464)
  'mileage_packages:package1_price_minor': { label: 'Package 1 price', kind: 'money' },
  'mileage_packages:package2_price_minor': { label: 'Package 2 price', kind: 'money' },
  'mileage_packages:package2_allowance_km': { label: 'Km included in Package 2', kind: 'km' },
  'mileage_packages:rate_minor_per_km': { label: 'Rate per extra km', kind: 'money' },
};

const CHANNEL_FIELDS: Record<string, { suffix: string; kind: Kind }> = {
  enabled: { suffix: '', kind: 'onOff' },
  'fee.enabled': { suffix: ' fee charged', kind: 'yesNo' },
  'fee.amount_minor': { suffix: ' fee', kind: 'money' },
  'fee.taxable': { suffix: ' fee taxable', kind: 'yesNo' },
  'fee.refundable': { suffix: ' fee refundable', kind: 'yesNo' },
};

function spec(setting: string, field: string): FieldSpec | null {
  if (setting === 'payment_channels') {
    const dot = field.indexOf('.');
    const id = field.slice(0, dot);
    const rest = CHANNEL_FIELDS[field.slice(dot + 1)];
    return rest ? { label: `${channelName(id)}${rest.suffix}`, kind: rest.kind } : null;
  }
  return FIELDS[`${setting}:${field}`] ?? null;
}

const number = new Intl.NumberFormat('en-MY');

function format(kind: Kind, value: unknown): string {
  if (value === null || value === undefined) {
    if (kind === 'maxMinutes') return 'no maximum';
    if (kind === 'version' || kind === 'picture') return 'none';
    return '—';
  }
  switch (kind) {
    case 'picture':
      return `picture …${String(value).slice(-6)}`;
    case 'money':
      return `RM ${(Number(value) / 100).toFixed(2)}`;
    case 'bps':
      return `${Number(value) / 100}%`;
    case 'km':
      return `${number.format(Number(value))} km`;
    case 'minutes':
    case 'maxMinutes':
      return `${number.format(Number(value))} min`;
    case 'mode':
      return MODE_LABELS[String(value)] ?? String(value);
    case 'onOff':
      return value ? 'On' : 'Off';
    case 'yesNo':
      return value ? 'Yes' : 'No';
    case 'precedence':
      return PRECEDENCE_LABELS[String(value)] ?? String(value);
    case 'frequency':
      return FREQUENCY_LABELS[String(value)] ?? String(value);
    default:
      return String(value);
  }
}

export function formatValue(setting: string, field: string, value: unknown): string {
  const s = spec(setting, field);
  return s ? format(s.kind, value) : String(value);
}

export function fieldLabel(setting: string, field: string): string {
  return spec(setting, field)?.label ?? field;
}

export interface FieldChange {
  field: string;
  from: unknown;
  to: unknown;
}

export function changeParts(
  setting: string,
  change: FieldChange,
): { label: string; from: string; to: string } {
  return {
    label: fieldLabel(setting, change.field),
    from: formatValue(setting, change.field, change.from),
    to: formatValue(setting, change.field, change.to),
  };
}

/** "Payment hold 20 min → 5 min, Auto-cancel Off → On" */
export function describeChanges(setting: string, changes: FieldChange[]): string {
  return changes
    .map((c) => {
      const p = changeParts(setting, c);
      return `${p.label} ${p.from} → ${p.to}`;
    })
    .join(', ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MYT_OFFSET_MS = 8 * 3600_000;

/**
 * "5 Oct, 06:35", always Malaysia time (FR-021). Built by hand rather than with `Intl`: locale data
 * differs between runtimes ("Sep" or "Sept"), and Malaysia has one fixed offset.
 */
export function formatMyt(iso: string | Date): string {
  const t = new Date(new Date(iso).getTime() + MYT_OFFSET_MS);
  const hh = String(t.getUTCHours()).padStart(2, '0');
  const mm = String(t.getUTCMinutes()).padStart(2, '0');
  return `${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}, ${hh}:${mm}`;
}
